import { cleanVal, getDbNumberForGrouping } from './formatUtils';
import { getTestOptionsForAssetType } from '../constants/AssetTestConfig';

/** Format a millisecond duration as HH:MM:SS (clamped at zero). */
export function formatRemaining(ms) {
  if (ms <= 0) return '00:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export const COMBINED_EXIT_EMERGENCY_TEST_OPTIONS = [
  { value: '90-minute Discharge', label: '90-minute Discharge' },
];

export const COMBINED_DB_RCD_TEST_OPTIONS = [
  { value: 'Push Button', label: 'Push Button' },
  { value: 'Operating Time', label: 'Operating Time', requiresMetrics: true, metricsLabel: 'Trip Time (ms)' },
  { value: 'Visual Check', label: 'Visual Check' },
];

export const COMBINED_FAN_HEATER_TEST_OPTIONS = [
  { value: 'Operational Check', label: 'Operational Check' },
];

export function getSessionTestOptions(assetType) {
  if (assetType === 'combined_exit_emergency') return COMBINED_EXIT_EMERGENCY_TEST_OPTIONS;
  if (assetType === 'combined_db_rcd') return COMBINED_DB_RCD_TEST_OPTIONS;
  if (assetType === 'combined_fan_heater') return COMBINED_FAN_HEATER_TEST_OPTIONS;
  return getTestOptionsForAssetType(assetType);
}

/** Default test type for the selected asset category (no dropdown). */
export function getDefaultTestTypeForAssetType(assetType) {
  if (!assetType) return '';
  if (assetType === 'combined_exit_emergency') return '90-minute Discharge';
  if (assetType === 'combined_db_rcd') return 'Push Button';
  const opts = getSessionTestOptions(assetType);
  return (opts && opts[0]?.value) ? opts[0].value : 'Visual Check';
}

/** Auto-detected test type for PDF reports (same as default for current session asset type). */
export function getAutoTestType(sessionAssetType) {
  return getDefaultTestTypeForAssetType(sessionAssetType);
}

export function snakeToTitleCase(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function groupRCDAssets(assets) {
  const groups = new Map();
  for (const a of assets) {
    const key = getDbNumberForGrouping(a) || cleanVal(a.building) || cleanVal(a.room) || 'Other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  return Array.from(groups.entries()).map(([groupName, items]) => ({ groupName, items }));
}

/**
 * Group key for building/room-grouped assets (Exit/Emergency and other single
 * types). Exported so consumers (e.g. the record modal stamping the discharge
 * start time) derive the same key the session workspace groups timers under.
 */
export function getBuildingRoomGroupKey(asset) {
  return cleanVal(asset?.building) || cleanVal(asset?.room) || 'Other';
}

/**
 * Does this session type group by board (DB number) rather than by building?
 *
 * Lives here so display code cannot drift from groupAssetsByType below: only these
 * two types key off the board, and everything else keys off building/room — which
 * means for everything else the group name already IS the location.
 */
export function isBoardGroupedSession(sessionAssetType) {
  const type = (sessionAssetType || '').toLowerCase();
  return type === 'combined_db_rcd' || type === 'rcd';
}

function groupOtherAssets(assets) {
  const groups = new Map();
  for (const a of assets) {
    const key = getBuildingRoomGroupKey(a);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  return Array.from(groups.entries()).map(([groupName, items]) => ({ groupName, items }));
}

function getDbRcdGroupKey(asset) {
  const type = (asset.asset_type || '').toLowerCase();
  if (type === 'distribution_board' || type === 'rcd') {
    return getDbNumberForGrouping(asset) || cleanVal(asset.building) || cleanVal(asset.room) || 'Other';
  }
  const code = (asset.equipment_code || asset.asset_code || '').trim();
  return code || cleanVal(asset.building) || cleanVal(asset.room) || 'Other';
}

function groupDbRcdAssets(assets) {
  const groups = new Map();
  for (const a of assets) {
    const key = getDbRcdGroupKey(a);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  return Array.from(groups.entries()).map(([groupName, items]) => {
    const sorted = [...items].sort((a, b) => {
      const aDb = (a.asset_type || '').toLowerCase() === 'distribution_board';
      const bDb = (b.asset_type || '').toLowerCase() === 'distribution_board';
      if (aDb && !bDb) return -1;
      if (!aDb && bDb) return 1;
      if (aDb && bDb) return 0;
      const codeA = (a.equipment_code || '').trim();
      const codeB = (b.equipment_code || b.asset_code || '').trim();
      const codeLeft = codeA || (a.asset_code || '').trim();
      const codeRight = codeB || (b.asset_code || '').trim();
      return codeLeft.localeCompare(codeRight, undefined, { numeric: true });
    });
    return { groupName, items: sorted };
  });
}

function groupFanHeaterAssets(assets) {
  const groups = new Map();
  for (const a of assets) {
    const key = (a.building || '').trim() || (a.room || '').trim() || 'Other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  return Array.from(groups.entries()).map(([groupName, items]) => {
    const sorted = [...items].sort((a, b) => {
      const roomA = (a.room || '').trim() || (a.building || '');
      const roomB = (b.room || '').trim() || (b.building || '');
      return roomA.localeCompare(roomB, undefined, { numeric: true });
    });
    return { groupName, items: sorted };
  });
}

export function groupAssetsByType(assets, assetType) {
  if (!assets?.length) return [];
  if (assetType === 'combined_db_rcd') return groupDbRcdAssets(assets);
  if (assetType === 'combined_fan_heater') return groupFanHeaterAssets(assets);
  const isRCD = (assetType || '').toLowerCase() === 'rcd';
  return isRCD ? groupRCDAssets(assets) : groupOtherAssets(assets);
}

/** Display label for a group (building/room aware for DB+RCD groups, null-stripped). */
export function getGroupDisplayLabel(groupName, items, sessionAssetType) {
  const stripNullAggressive = (str) => (str || '').replace(/null/gi, '').trim().replace(/\s+/g, ' ').trim();
  const isDbRcdGroup = sessionAssetType === 'combined_db_rcd';
  const dbAsset = isDbRcdGroup && items?.length ? items.find((a) => (a.asset_type || '').toLowerCase() === 'distribution_board') : null;
  const bld = cleanVal(dbAsset?.building);
  const rm = cleanVal(dbAsset?.room);
  const locationDesc = stripNullAggressive([bld, rm].filter(Boolean).join(' ')) || '';
  const cleanName = stripNullAggressive(cleanVal(groupName));
  return stripNullAggressive([cleanName, locationDesc].filter(Boolean).join(' — ')) || groupName || 'Group';
}

export function filterExitEmergencyAssets(assets) {
  if (!assets?.length) return [];
  return assets.filter(
    (a) => (a.asset_type || '').toLowerCase() === 'exit_light' || (a.asset_type || '').toLowerCase() === 'emergency_light'
  );
}

export function filterDbRcdAssets(assets) {
  if (!assets?.length) return [];
  return assets.filter((a) => {
    const t = (a.asset_type || '').toLowerCase();
    return t === 'distribution_board' || t === 'rcd';
  });
}

export function filterFanHeaterAssets(assets) {
  if (!assets?.length) return [];
  return assets.filter((a) => {
    const t = (a.asset_type || '').toLowerCase();
    return t === 'ceiling_fan' || t === 'wall_heater';
  });
}

export function filterBySingleAssetType(assets, assetType) {
  if (!assets?.length || !assetType) return [];
  const lower = assetType.toLowerCase();
  return assets.filter((a) => (a.asset_type || '').toLowerCase() === lower);
}

export function isQuickPassAllowed(asset, sessionTestType, sessionAssetType) {
  if (sessionTestType === 'Operating Time') return false;
  if (sessionAssetType === 'combined_db_rcd') {
    const type = (asset.asset_type || '').toLowerCase();
    if (sessionTestType === 'Push Button') return type === 'rcd';
    if (sessionTestType === 'Visual Check') return type === 'distribution_board';
    return false;
  }
  return true;
}

export function getQuickPassTooltip(asset, sessionTestType, sessionAssetType, isOperatingTime, tested) {
  if (tested) return 'Already tested this session';
  if (isOperatingTime) return 'Use Fail for Operating Time (trip time required)';
  if (sessionAssetType === 'combined_db_rcd') {
    const type = (asset.asset_type || '').toLowerCase();
    if (sessionTestType === 'Push Button' && type === 'distribution_board') return 'Test not applicable to this asset type';
    if (sessionTestType === 'Visual Check' && type === 'rcd') return 'Test not applicable to this asset type';
  }
  return 'Record Pass';
}
